import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Link } from '@vapor/material'
import { GridColDef } from '@mui/x-data-grid'
import { GraphQLDataGrid } from 'hic-components'
import { useTranslation } from 'react-i18next'
import { CervedAdvancedSearchDocument, CervedAdvancedSearchQuery, CervedAdvancedSearchQueryVariables, CervedSubjectFragment } from 'src/api/api.generated'

export type CervedSearchFilter =
  | {
      type: 'SEARCH_TEXT'
      searchText?: string
    }
  | {
      type: 'VAT_NUMBER'
      vatNumber?: string
    }
  | undefined

export type CervedResultsDialogProps = {
  filter: CervedSearchFilter
  onClose: () => void
  onCervedSubjectSelected: (subject: CervedSubjectFragment) => void
}

export default function CervedResultsDialog(props: CervedResultsDialogProps) {
  const { filter, onClose, onCervedSubjectSelected } = props
  const { t } = useTranslation()
  const columns = useSubjectColumns(onCervedSubjectSelected, onClose)
  return (
    <Dialog open={filter !== undefined} onClose={onClose} maxWidth={'lg'} fullWidth>
      <DialogTitle>{t('pms.components.cerved_results.title')}</DialogTitle>
      <DialogContent>
        <div style={{ height: 500 }}>
          <GraphQLDataGrid<CervedAdvancedSearchQuery, CervedSubjectFragment, CervedAdvancedSearchQueryVariables> //
            columns={columns}
            data={data}
            getRowId={(row) => row.subject_id}
            query={CervedAdvancedSearchDocument}
            pagination={false}
            variables={() => ({
              searchText: filter?.type === 'SEARCH_TEXT' ? filter.searchText : undefined,
              vatNumber: filter?.type === 'VAT_NUMBER' ? filter.vatNumber : undefined,
            })}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('commons.cancel')}</Button>
      </DialogActions>
    </Dialog>
  )
}

function data(response: CervedAdvancedSearchQuery | undefined): CervedSubjectFragment[] {
  return (
    response?.cervedAdvancedSearch?.subjects?.reduce((previousValue: CervedSubjectFragment[], currentValue: CervedSubjectFragment | null | undefined) => {
      if (currentValue) return previousValue.concat(currentValue)
      return previousValue
    }, []) || []
  )
}

const useSubjectColumns = (onCervedSubjectSelected: (subject: CervedSubjectFragment) => void, onClose: () => void): GridColDef[]=> {
  const { t } = useTranslation()

  return [
    {
      field: 'business_name',
      headerName: t('pms.components.cerved_results.subject.business_name'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        const subject = params.row as CervedSubjectFragment
        const name = subject.company_info?.business_name || subject.person_info?.name
        return name?.toString()
      },
    },
    {
      field: 'city',
      headerName: t('pms.components.cerved_results.subject.city'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        const subject = params.row as CervedSubjectFragment
        const street = subject.address?.city?.description
        return street ?? ''
      },
    },
    {
      field: 'address',
      headerName: t('pms.components.cerved_results.subject.address'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        const subject = params.row as CervedSubjectFragment
        const address = subject.address?.street?.description
        return address ?? ''
      },
    },
    {
      field: 'province',
      headerName: t('pms.components.cerved_results.subject.province'),
      width: 100,
      disableColumnMenu: true,
      renderCell: (params) => {
        const subject = params.row as CervedSubjectFragment
        const vatNumber = subject.address?.province?.code
        return vatNumber ?? ''
      },
    },
    {
      field: 'vatNumber',
      headerName: t('pms.components.cerved_results.subject.vat_number'),
      width: 125,
      disableColumnMenu: true,
      renderCell: (params) => {
        const subject = params.row as CervedSubjectFragment
        const vatNumber = subject.company_info?.vat_number
        return vatNumber?.toString()
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('commons.table_actions'),
      width: 125,
      getActions: (params) => [
        <Link
          onClick={() => {
            onCervedSubjectSelected(params.row)
            onClose()
          }}
        >
          {t('pms.components.cerved_results.subject.get_data')}
        </Link>,
      ],
    },
  ]
}
